import { ref, onUnmounted } from 'vue'
import canAutoPlay from 'can-autoplay'
import Player from '@/common/player/scalableplayer'
import { isAdultContent, userAdultVerifiedInfo } from '@/common/utils/adult'
import { IS_INSTANT_PLAYER_MUTE } from '@/common/player/const'

export const useInstantPlayer = () => {
  const playDelayTimer = ref(null)
  const player = ref(null)
  const lastPlayTime = ref(0)
  const isCancelled = ref(false)

  // returns
  const isMute = ref(false)
  const isSpinner = ref(false)
  const isPlaying = ref(false)

  /**
   * Instant Player 로드 완료 콜백
   */
  const handlePlayerLoaded = () => {
    clearTimeout(playDelayTimer.value)
    isSpinner.value = false
    isPlaying.value = true
    player.value?.setMute(isMute.value)
  }

  /**
   * Instant Player Timeout (1분 이후) 콜백
   */
  const handlePlayerTimeout = () => {
    clearTimeout(playDelayTimer.value)
    isSpinner.value = false
  }

  /**
   * Instant Player Error 콜백
   */
  const handlePlayerError = () => {
    console.warn(`❌ Video can not Play. Please Check the Error Console`)
    stopPlay()
  }

  /**
   * throttle 여부 반환
   * 시간 비교하여 500ms 이하에서 플레이 시도 할 시 중복 트리거 발생 방지
   * @returns {boolean}
   */
  const isThrottled = () => {
    const now = Date.now()
    const PLAY_THROTTLE_MS = 500
    if (now - lastPlayTime.value < PLAY_THROTTLE_MS) {
      return true
    }

    lastPlayTime.value = now
    return false
  }

  /**
   * 성인 인증 여부 반환
   * @param targetAge
   * @returns {Promise<boolean>}
   */
  const checkAdultPermission = async (targetAge = 0) => {
    if (!isAdultContent(`${targetAge}`)) {
      return true
    }

    const { isAdultVerified } = await userAdultVerifiedInfo()

    return isAdultVerified
  }

  /**
   * Instant Player 를 로드 -> 렌더링 -> 플레이
   * @param options
   *
   */
  const renderPlayer = (options) => {
    const { playerElement = null, contentType = '', contentId = '' } = options

    player.value = null
    player.value = new Player(playerElement, 'i', 'i')
    player.value.load(contentType, contentId)

    player.value.setLoadedCallback(() => handlePlayerLoaded())
    player.value.setTimeoutCallback(() => handlePlayerTimeout())
    player.value.setErrorCallback(() => handlePlayerError())
  }

  /**
   * Instant Player 제거 및 플레이어 정지
   */
  const stopPlay = () => {
    isCancelled.value = true
    clearTimeout(playDelayTimer.value)

    if (player.value === null) {
      return
    }

    isSpinner.value = false
    isPlaying.value = false

    player.value.destructor()
    player.value = null
  }

  /**
   * Instant Player 를 렌더링 (시도)하는 메서드
   * @param options
   * @param options.playerElement : HTMLElement | null
   * @param options.contentType : string
   * @param options.contentId : string
   * @param options.targetAge : number
   * @returns {Promise<void>}
   */
  const startPlay = async (options) => {
    const { targetAge = 0 } = options
    const throttled = isThrottled()

    if (throttled) {
      return
    }

    isCancelled.value = false
    isMute.value =
      (sessionStorage.getItem(IS_INSTANT_PLAYER_MUTE) ?? 'true') === 'true'

    try {
      const canAutoPlayVideo = await canAutoPlay.video()
      if (!canAutoPlayVideo?.result) {
        console.warn(
          `🔉 Autoplay can not allowed, Switching to mute mode and play video`,
        )
        isMute.value = true
      }

      const canPlayAdult = await checkAdultPermission(targetAge)
      if (!canPlayAdult) {
        isPlaying.value = false
        console.warn(
          `🔞 Video is Adult Content, User is not an Adult Verified can not play`,
        )
        return
      }

      if (isCancelled.value) {
        console.warn(
          '🚫 Playback Cancelled before render player with throttling',
        )
        return
      }

      playDelayTimer.value = setTimeout(() => {
        if (!isCancelled.value) {
          renderPlayer(options)
        }
      }, 1000)
    } catch (error) {
      console.error(`Error Video Can not Autoplay ${error.message}`)
      isPlaying.value = false
    }
  }

  /**
   * Instant Player Mute 여부 토글
   */
  const toggleMute = () => {
    isMute.value = !isMute.value
    sessionStorage.setItem(IS_INSTANT_PLAYER_MUTE, `${isMute.value}`)
    player.value?.setMute(isMute.value)
  }

  onUnmounted(() => stopPlay())

  return {
    isMute,
    isSpinner,
    isPlaying,
    startPlay,
    stopPlay,
    toggleMute,
  }
}
